<template>
  <div class="page-community">
    <div class="saas-page-head page-community__head">
      <h1 class="saas-page-head__title" v-html="formattedTitle"></h1>
      <p class="saas-page-head__description">
        {{ page.intro_text }}
      </p>
      <div class="saas-page-head__actions">
        <CmsLink
          v-for="button in page.intro_buttons"
          :key="button.id"
          :url-or-path="button.value.url_or_path"
          :class="`button button--${button.value.type} button--large`"
        >
          {{ button.value.text }}
        </CmsLink>
      </div>
      <div class="page-community__head-image">
        <img
          :src="page.intro_image.url"
          :alt="page.intro_image.alt"
          :srcset="`${page.intro_retina_image.url} 2x`"
        />
      </div>
    </div>
    <div class="page-community__section page-community__section--founder">
      <div class="page-community__section-inner">
        <div class="page-community__content-left">
          <div class="page-community__founder-photo">
            <img
              :src="page.founder_image.url"
              :alt="page.founder_image.alt"
              :srcset="`${page.founder_retina_image.url} 2x`"
            />
            <span class="page-community__founder-photo-caption">{{
              page.founder_image_text
            }}</span>
          </div>
        </div>

        <div class="page-community__content-right">
          <h2 class="saas-striped-title saas-striped-title--align-left">
            Hey there!
          </h2>
          <p class="page-community__content-text">
            {{ page.founder_text }}
          </p>
          <div>
            <CmsLink
              v-for="button in page.founder_buttons"
              :key="button.id"
              :url-or-path="button.value.url_or_path"
              :class="`button button--${button.value.type} button--large`"
            >
              {{ button.value.text }}
            </CmsLink>
          </div>
        </div>
      </div>
    </div>

    <div
      class="page-community__section page-community__section--dark page-community__section--newsletter"
    >
      <div class="page-community__section-inner">
        <div class="page-community__content-left">
          <h2 class="saas-striped-title saas-striped-title--align-left">
            {{ page.newsletter_title }}
          </h2>
          <p class="page-community__content-text">
            {{ page.newsletter_text }}
          </p>
        </div>

        <div class="page-community__content-right">
          <NewsletterSignup
            class="page-community__newsletter-input"
          ></NewsletterSignup>
          <div class="page-community__social">
            <span>Stay connected on social:</span>
            <div class="page-community__social-icons">
              <a
                class="page-community__social-icon"
                href="https://twitter.com/baserow"
                target="_new"
              >
                <i class="baserow-icon-twitter"></i>
              </a>

              <a
                class="page-community__social-icon"
                href="https://www.linkedin.com/company/baserow/"
                target="_new"
              >
                <i class="baserow-icon-linkedin"></i>
              </a>

              <a
                class="page-community__social-icon"
                href="https://www.youtube.com/@baserow"
                target="_new"
              >
                <i class="iconoir-youtube"></i>
              </a>

              <a
                class="page-community__social-icon"
                href="https://www.reddit.com/r/baserow/"
                target="_new"
              >
                <i class="baserow-icon-reddit"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      id="community-forum"
      class="page-community__section page-community__section--community"
    >
      <div class="page-community__section-inner">
        <div class="page-community__content-left">
          <h2 class="saas-striped-title saas-striped-title--align-left">
            {{ page.forum_title }}
          </h2>
          <p class="page-community__content-text">
            {{ page.forum_text }}
          </p>
        </div>
        <div class="page-community__content-right">
          <CmsLink
            v-for="button in page.forum_buttons"
            :key="button.id"
            :page="button.value.page"
            :url-or-path="button.value.url_or_path"
            class="page-community__link"
          >
            <span class="page-community__link-icon">
              <i :class="button.value.icon_class"></i>
            </span>
            <i class="page-community__hover-icon iconoir-arrow-right"></i>
            <strong>{{ button.value.name }}</strong>
            <p>{{ button.value.text }}</p>
          </CmsLink>
        </div>
      </div>
    </div>

    <div class="page-community__section page-community__section--developers">
      <div class="page-community__section-inner">
        <div class="page-community__content-left">
          <h2 class="saas-striped-title saas-striped-title--align-left">
            {{ page.developers_title }}
          </h2>
          <p class="page-community__content-text">
            {{ page.developers_text }}
          </p>
        </div>

        <div class="page-community__content-right">
          <CmsLink
            v-for="button in page.developers_buttons"
            :key="button.id"
            :page="button.value.page"
            :url-or-path="button.value.url_or_path"
            class="page-community__link"
          >
            <span class="page-community__link-icon">
              <i :class="button.value.icon_class"></i>
            </span>
            <i class="page-community__hover-icon iconoir-arrow-right"></i>
            <strong>{{ button.value.name }}</strong>
            <p>{{ button.value.text }}</p>
          </CmsLink>
        </div>
      </div>
    </div>

    <div class="page-community__section-inner">
      <div class="saas-cta-box page-community__cta-box-with-margin">
        <div>
          <span class="saas-cta-box__title">{{ page.start_title }}</span>
          <p class="saas-cta-box__text">
            {{ page.start_text }}
          </p>
        </div>

        <div class="saas-cta-box__buttons">
          <CmsLink
            v-for="button in page.start_buttons"
            :key="button.id"
            :url-or-path="button.value.url_or_path"
            :class="`button button--${button.value.type} button--large`"
          >
            {{ button.value.text }}
          </CmsLink>
        </div>
      </div>
    </div>

    <div
      class="page-community__section page-community__section--dark page-community__section--featured-posts"
    >
      <div class="page-community__section-inner">
        <h2 class="saas-striped-title">
          {{ page.featured_posts_title }}
        </h2>

        <div class="page-community__posts">
          <a
            v-for="post in page.featured_posts_items"
            :key="post.id"
            :href="post.value.url"
            class="page-community__post"
            target="_blank"
          >
            <h3>{{ post.value.title }}</h3>
            <div class="page-community__post-metadata">
              <div class="page-community__post-icon">
                <i
                  v-if="isYoutube(post.value.url)"
                  class="iconoir-youtube page-community__post-icon-yt"
                ></i>
                <img
                  v-if="isBaserowCommunity(post.value.url)"
                  src="@baserow/modules/core/static/img/logoOnly.svg"
                  alt="Baserow logo"
                  width="12"
                  height="12"
                />
                <img
                  v-if="isNoCodeDevs(post.value.url)"
                  src="@saas/assets/images/nocode-newsletter.svg"
                  alt="Baserow logo"
                  width="16"
                  height="14"
                />
              </div>
              <p>
                <strong>{{ post.value.author }}</strong> on
                {{ post.value.channel }}
              </p>
            </div>
          </a>
        </div>
      </div>
    </div>

    <div class="page-community__section page-community__section--wall">
      <div class="page-community__section-inner">
        <h2 class="saas-striped-title">{{ page.social_media_title }}</h2>

        <div class="page-community__testimonials">
          <a
            v-for="item in page.social_media_items"
            :key="item.id"
            :href="item.social_media_items"
            target="_new"
            class="page-community__post"
          >
            <h3>{{ item.value.title }}</h3>
            <div class="page-community__post-metadata">
              <div
                v-if="isTwitter(item.value.url) || isHackerNews(item.value.url)"
                class="page-community__post-icon"
              >
                <i
                  v-if="isTwitter(item.value.url)"
                  class="baserow-icon-twitter"
                ></i>
                <i
                  v-if="isHackerNews(item.value.url)"
                  class="page-community__post-icon-hn"
                  >Y</i
                >
              </div>
              <p>{{ item.value.author }}</p>
            </div>
          </a>
        </div>
      </div>
    </div>

    <div
      class="page-community__section page-community__section--dark page-community__section--love"
    >
      <div class="page-community__section-inner">
        <div class="page-community__content-left">
          <h2 class="saas-striped-title saas-striped-title--align-left">
            {{ page.share_title }}
          </h2>
          <p class="page-community__content-text">
            {{ page.share_text }}
          </p>
        </div>

        <div class="page-community__content-right">
          <iframe
            ref="iframe"
            class="page-community__feedback-iframe"
            src="https://baserow.io/form/CBdaMe1SwdGD-qDJOUEnqc-o1ll2inOYA5EFY8pi9fo"
            height="650"
          ></iframe>
        </div>
      </div>

      <div class="page-community__section-inner">
        <div class="saas-cta-box">
          <img
            v-if="page.community_manager_image"
            class="saas-cta-box__image"
            :src="page.community_manager_image.url"
            :alt="page.community_manager_image.alt"
            :srcset="`${page.community_manager_retina_image.url} 2x`"
            width="120"
            height="120"
          />

          <div>
            <span class="saas-cta-box__title">{{
              page.community_manager_title
            }}</span>
            <div
              class="saas-cta-box__text"
              v-html="page.community_manager_text"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <FooterJoin
      :hide="page.hide_footer"
      :title="page.footer_title"
      :text="page.footer_text"
      :buttons="page.footer_buttons"
    ></FooterJoin>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'

import NewsletterSignup from '@saas/components/NewsletterSignup'
import cmsPage from '@saas/mixins/cmsPage'
import CmsLink from '@saas/components/cms/CmsLink'
import FooterJoin from '@saas/components/footer/FooterJoin'

export default {
  components: { NewsletterSignup, CmsLink, FooterJoin },
  mixins: [cmsPage],
  layout: 'saas',
  computed: {
    formattedTitle() {
      const md = MarkdownIt({ html: false, breaks: false })
      return md.renderInline(this.page.intro_title)
    },
  },
  methods: {
    isHackerNews(value) {
      return value.toLowerCase().startsWith('https://news.ycombinator.com')
    },
    isTwitter(value) {
      return value.toLowerCase().startsWith('https://twitter.com')
    },
    isYoutube(value) {
      return (
        value.toLowerCase().startsWith('https://youtu.be') ||
        value.toLowerCase().startsWith('https://www.youtube.com')
      )
    },
    isBaserowCommunity(value) {
      return value.toLowerCase().includes('baserow.io')
    },
    isNoCodeDevs(value) {
      return value.toLowerCase().startsWith('https://newsletter.nocodedevs.com')
    },
  },
}
</script>
